<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactsAPI from 'dashboard/api/contacts';
import ForwardsAPI from 'dashboard/api/whatsappForwards';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ messageId: { type: Number, required: true } });
const emit = defineEmits(['close']);
const { t } = useI18n();
const dialog = ref(null);
const query = ref('');
const results = ref([]);
const selected = ref(null);
const searching = ref(false);
const searched = ref(false);
const sending = ref(false);
const requestId = ref(crypto.randomUUID());
let revision = 0;
onMounted(() => dialog.value.open());

async function search() {
  const current = ++revision;
  searched.value = false;
  selected.value = null;
  results.value = [];
  if (!query.value.trim()) return;
  searching.value = true;
  try {
    const { data } = await ContactsAPI.search(query.value.trim());
    if (current !== revision) return;
    results.value = data.payload.filter(contact => contact.phone_number);
    searched.value = true;
  } catch {
    if (current === revision)
      useAlert(t('WHATSAPP_CONTACT_CARDS.SEARCH_ERROR'));
  } finally {
    if (current === revision) searching.value = false;
  }
}

function select(contact) {
  selected.value = contact;
  requestId.value = crypto.randomUUID();
}

async function send() {
  if (!selected.value || sending.value) return;
  sending.value = true;
  try {
    await ForwardsAPI.send(props.messageId, selected.value.id, requestId.value);
    useAlert(t('WHATSAPP_MESSAGING.QUEUED'));
    sending.value = false;
    dialog.value.close();
  } catch {
    useAlert(t('WHATSAPP_MESSAGING.FORWARD_ERROR'));
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('WHATSAPP_MESSAGING.FORWARD')"
    :description="t('WHATSAPP_MESSAGING.FORWARD_DESCRIPTION')"
    :confirm-button-label="t('WHATSAPP_MESSAGING.FORWARD')"
    :cancel-button-label="t('WHATSAPP_CONTACT_CARDS.CANCEL')"
    :disable-confirm-button="!selected"
    :is-loading="sending"
    overflow-y-auto
    @confirm="send"
    @close="
      revision++;
      emit('close');
    "
  >
    <div class="grid min-w-0 gap-4">
      <div class="flex items-end gap-2" @keydown.enter.prevent.stop="search">
        <Input
          v-model="query"
          class="min-w-0 flex-1"
          :label="t('WHATSAPP_CONTACT_CARDS.SEARCH')"
          :placeholder="t('WHATSAPP_CONTACT_CARDS.SEARCH_PLACEHOLDER')"
          :disabled="sending"
        />
        <Button
          type="button"
          :label="t('WHATSAPP_CONTACT_CARDS.SEARCH_BUTTON')"
          :is-loading="searching"
          :disabled="sending"
          @click="search"
        />
      </div>
      <p
        v-if="searched && !results.length"
        class="text-sm text-n-slate-11"
        role="status"
      >
        {{ t('WHATSAPP_MESSAGING.NO_CONTACTS') }}
      </p>
      <div
        class="grid max-h-64 gap-2 overflow-y-auto"
        role="group"
        :aria-label="t('WHATSAPP_MESSAGING.RECIPIENT')"
      >
        <button
          v-for="contact in results"
          :key="contact.id"
          type="button"
          :disabled="sending"
          :aria-pressed="selected?.id === contact.id"
          class="flex min-h-12 min-w-0 items-center justify-between gap-3 rounded-lg border p-3 text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :class="
            selected?.id === contact.id
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak hover:bg-n-alpha-2'
          "
          @click="select(contact)"
        >
          <span
            class="min-w-0 break-words text-sm font-medium text-n-slate-12"
            >{{ contact.name }}</span
          >
          <span class="shrink-0 text-sm text-n-slate-11" dir="ltr">{{
            contact.phone_number
          }}</span>
        </button>
      </div>
      <p
        v-if="selected"
        class="break-words text-sm text-n-slate-12"
        role="status"
      >
        {{ t('WHATSAPP_MESSAGING.SEND_TO', { name: selected.name }) }}
      </p>
    </div>
  </Dialog>
</template>
